import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress
} from '@mui/material';
import { updatePaper } from '../../services/indianExamsService';

export default function AdminEditPaperModal({ open, paper, onClose, onSave }) {
  const [formData, setFormData] = useState({
    title: '',
    year: '',
    category: '',
    examId: '',
    durationMinutes: '',
    questionCount: '',
    isActive: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (paper) {
      setFormData({
        title: paper.title || '',
        year: paper.year || '',
        category: paper.category || '',
        examId: paper.examId || '',
        durationMinutes: paper.durationMinutes || '',
        questionCount: paper.questionCount || '',
        isActive: paper.isActive !== false
      });
    }
  }, [paper]);

  const handleChange = (field) => (e) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleToggle = (e) => {
    setFormData(prev => ({ ...prev, isActive: e.target.checked }));
  };

  const handleSubmit = async () => {
    if (!formData.examId) {
      setError('Exam ID is required for saving.');
      return;
    }
    setLoading(true);
    setError('');
    
    // Prepare patch
    const patch = {
      title: formData.title,
      year: Number(formData.year) || undefined,
      category: formData.category,
      durationMinutes: Number(formData.durationMinutes) || undefined,
      questionCount: Number(formData.questionCount) || undefined,
      isActive: formData.isActive
    };

    try {
      const res = await updatePaper(formData.examId, paper.id || paper.paperId, patch);
      if (res && res.success) {
        onSave(res.paper || { ...paper, ...patch });
      } else {
        setError(res?.error || 'Failed to update paper');
      }
    } catch (err) {
      setError(err.message || 'Error updating paper');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Paper Metadata</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Title"
              value={formData.title}
              onChange={handleChange('title')}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Year"
              type="number"
              value={formData.year}
              onChange={handleChange('year')}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Category"
              value={formData.category}
              onChange={handleChange('category')}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Exam ID"
              value={formData.examId}
              onChange={handleChange('examId')}
              helperText="E.g. ssc_cgl_tier1"
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Duration (Mins)"
              type="number"
              value={formData.durationMinutes}
              onChange={handleChange('durationMinutes')}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Question Count"
              type="number"
              value={formData.questionCount}
              onChange={handleChange('questionCount')}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={formData.isActive} onChange={handleToggle} />}
              label="Is Active (Visible to users)"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
